import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Table as TableComponent, UncontrolledTooltip } from 'reactstrap';
import { useTranslation } from 'react-i18next';

import { ReactComponent as FilterIcon } from 'assets/icon-filter.svg';
import { ReactComponent as FilterAppliedIcon } from 'assets/icon-filter-applied.svg';
import { ReactComponent as ClientIcon } from 'assets/icon-consulting.svg';

import { ButtonIcon } from 'shared/components/Buttons';
import { ReactComponent as UnlockIcon } from 'assets/icon-unlock.svg';
import { ReactComponent as LockIcon } from 'assets/icon-lock.svg';
import FilterDropdownInput from 'shared/components/FilterDropdownInput';
import FilterMultiSelect from 'shared/components/FilterMultiSelect';
import Pagination from 'shared/components/Pagination';
import MainSpinner from 'shared/components/MainSpinner';

import { colors } from 'shared/defaultTheme';

import {
  TableHeader,
  TableHeaderText,
  TableBodyText,
} from 'shared/components/SharedStyles/SharedStyles';

import classnames from 'classnames';

const PAGE_SIZE = 10;

const TH = ({ title, column, btnText, columnVal, options, handleColumnFilter, filterType }) => {
  const isColumnValFilled = Array.isArray(columnVal) ? columnVal.length > 0 : columnVal;
  return (
    <th>
      <TableHeader
        className={classnames({
          'd-flex justify-content-center': column === 'contactState',
        })}
      >
        <TableHeaderText>{title}</TableHeaderText>
        <ButtonIcon
          title={btnText}
          id={`filter-${column}`}
          className="button-icon"
          Icon={() =>
            isColumnValFilled ? (
              <FilterAppliedIcon fill={colors.black} />
            ) : (
              <FilterIcon fill={colors.black} />
            )
          }
          onClick={() => null}
        />
        {filterType === 'single' && (
          <FilterDropdownInput
            dropdownOptions={options}
            target={`filter-${column}`}
            fieldSelectedOption={column}
            fieldSearchText={column}
            column={column}
            onSearch={handleColumnFilter}
          />
        )}
        {filterType === 'multi' && (
          <FilterMultiSelect
            options={options}
            target={`filter-${column}`}
            fieldSearch={column}
            column={column}
            onSearch={handleColumnFilter}
          />
        )}
      </TableHeader>
    </th>
  );
};

TH.propTypes = {
  title: PropTypes.string.isRequired,
  column: PropTypes.string.isRequired,
  btnText: PropTypes.string.isRequired,
  columnVal: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)])
    .isRequired,
  options: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object])).isRequired,
  handleColumnFilter: PropTypes.func.isRequired,
  filterType: PropTypes.string.isRequired,
};

const TD = ({ children, index, toolTipText, column }) => {
  return (
    <td>
      <TableBodyText
        className={classnames('w-auto', {
          'text-center': column === 'contactState',
          'text-limit': column === 'description',
        })}
      >
        <p id={`${column}-${index}`}>{children}</p>
        {column !== 'contactState' && (
          <UncontrolledTooltip placement="top" target={`${column}-${index}`}>
            {toolTipText}
          </UncontrolledTooltip>
        )}
      </TableBodyText>
    </td>
  );
};

TD.propTypes = {
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  index: PropTypes.number.isRequired,
  toolTipText: PropTypes.string.isRequired,
  column: PropTypes.string.isRequired,
};

const Table = ({ loading }) => {
  const { clientAccessManagerContacts, clientAccessManagerSearchText } = useSelector(
    (state) => state.clientAccessManager
  );
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [contacts, setContacts] = useState([]);
  const [currentPageContacts, setCurrentPageContacts] = useState([]);
  const [columnSearch, setColumnSearch] = useState({
    name: '',
    surname: '',
    mail: '',
    phone: '',
    description: '',
    clientName: '',
    contactState: '',
  });

  const filterContactsBySearch = () => {
    const allowedSearchStringFilters = [
      'name',
      'surname',
      'mail',
      'phone',
      'description',
      'clientName',
      'contactState',
    ];

    const filtered = clientAccessManagerContacts.filter((obj) =>
      Object.keys(obj).some((k) => {
        if (!obj[k] || !allowedSearchStringFilters.includes(k)) return false;
        return JSON.stringify(obj[k])
          .toLowerCase()
          .includes(clientAccessManagerSearchText.toLowerCase());
      })
    );
    return filtered;
  };

  const hasFilters = () => {
    return Object.values(columnSearch).some((value) => value.length > 0);
  };

  const filterByColums = (contactsArray) => {
    if (!hasFilters()) return contactsArray;
    const filteredByColums = contactsArray.filter((obj) => {
      let isValidRow = true;
      Object.entries(columnSearch).forEach((column) => {
        if (isValidRow) {
          if (column[0] !== 'contactState') {
            if (!(column[0] in obj)) {
              if (column[1].length > 0) {
                isValidRow = false;
              } else {
                isValidRow = true;
              }
            } else if (
              column[1] &&
              !obj[column[0]]?.toLowerCase().includes(column[1]?.toLowerCase())
            ) {
              isValidRow = false;
            }
          }

          if (
            column[0] === 'contactState' &&
            column[1].length > 0 &&
            !column[1].includes(obj[column[0]])
          ) {
            isValidRow = false;
          }
        }
      });
      return isValidRow;
    });
    return filteredByColums;
  };

  useEffect(() => {
    if (clientAccessManagerSearchText) {
      const filteredBySearch = filterContactsBySearch();
      const filteredByColumns = filterByColums(filteredBySearch);
      setContacts(filteredByColumns);
    } else {
      const filteredByColumns = filterByColums(clientAccessManagerContacts);
      setContacts(filteredByColumns);
    }
  }, [clientAccessManagerContacts, clientAccessManagerSearchText, columnSearch]);

  useEffect(() => {
    setCurrentPage(1);
    setLastPage(Math.ceil(contacts.length / PAGE_SIZE));
    setCurrentPageContacts(contacts.slice(0, PAGE_SIZE));
  }, [contacts]);

  const onGoToPage = (page) => {
    setCurrentPage(page);
    setCurrentPageContacts(contacts.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE));
  };

  const onFirstPage = () => {
    onGoToPage(1);
  };

  const onLastPage = () => {
    onGoToPage(lastPage);
  };

  const onNextPage = () => {
    const page = currentPage + 1;
    if (page > lastPage) return;
    setCurrentPage(page);
    onGoToPage(page);
  };

  const onPreviousPage = () => {
    const page = currentPage - 1;
    if (page <= 0) return;
    onGoToPage(page);
  };

  const getColumnOptions = (fieldName) => {
    const contactStateNames = {
      sended: t('client_management_access_send'),
      access: t('client_management_access_active'),
      created: t('client_management_no_has_access'),
    };

    const values = Array.from(new Set(contacts.map((currentContact) => currentContact[fieldName])));
    return values
      .filter((x) => x && x !== '')
      .map((value) => ({
        value,
        name: fieldName === 'contactState' ? contactStateNames[value] : value,
      }));
  };

  const handleColumnFilter = ({ selectedOption, selectedOptions, searchText, column }) => {
    const search = selectedOptions || selectedOption || searchText;
    if (!column) return;
    setColumnSearch({ ...columnSearch, [column]: search });
    const filtered = contacts.filter((obj) => {
      if (!obj[column] || !search) return false;
      return selectedOptions
        ? selectedOptions.includes(obj[column])
        : JSON.stringify(obj[column]).toLowerCase().includes(search.toLowerCase());
    });
    setContacts(filtered);
  };

  const getIcon = (contactState) => {
    const contactStates = {
      sended: {
        icon: <LockIcon id="sended" fill={colors.indochine} height={20} width={20} />,
        tooltip: t('client_management_access_send'),
      },
      access: {
        icon: <UnlockIcon id="access" fill={colors.bilbao} height={20} width={20} />,
        tooltip: t('client_management_access_active'),
      },
      created: {
        icon: <LockIcon id="created" fill={colors.error} height={20} width={20} />,
        tooltip: t('client_management_no_has_access'),
      },
    };

    return (
      <>
        {contactState && contactStates[contactState] ? (
          contactStates[contactState].icon
        ) : (
          <LockIcon id="created" fill={colors.error} height={20} width={20} />
        )}
        <UncontrolledTooltip placement="top" target={contactState || 'created'}>
          {contactState ? contactStates[contactState].tooltip : contactStates.created.tooltip}
        </UncontrolledTooltip>
      </>
    );
  };

  const headers = [
    { column: 'name', filterType: 'single' },
    { column: 'surname', filterType: 'single' },
    { column: 'mail', filterType: 'single' },
    { column: 'phone', filterType: 'single' },
    { column: 'description', filterType: 'single' },
    { column: 'clientName', filterType: 'single' },
    { column: 'contactState', filterType: 'multi' },
  ];

  return (
    <div className="control-panel__table table-clients">
      <TableComponent>
        <thead>
          <tr>
            {headers.map((header) => (
              <TH
                key={header.column}
                title={t(`client_access_manager_${header.column}_column`)}
                column={header.column}
                btnText={t('general_button_filter')}
                columnVal={columnSearch[header.column]}
                options={getColumnOptions(header.column)}
                handleColumnFilter={handleColumnFilter}
                filterType={header.filterType}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {currentPageContacts.map((currentContact, index) => (
            <tr
              key={`${currentContact.clientId}-${currentContact.mail}`}
              className="action-row-client"
              onClick={() => null}
            >
              {headers.map((header) => (
                <TD
                  key={`${header.column}-${currentContact.mail}`}
                  index={index}
                  column={header.column}
                  toolTipText={`${currentContact[header.column]}`}
                >
                  {header.column === 'contactState'
                    ? getIcon(currentContact[header.column])
                    : currentContact[header.column] || ''}
                </TD>
              ))}
            </tr>
          ))}
        </tbody>
      </TableComponent>
      {!loading && contacts.length === 0 && (
        <div className="d-flex flex-column justify-content-center align-items-center">
          <ClientIcon width={57} fill={colors.suvaGrey} height={65} className="mb-2" />
          <span>{t('client_access_manager_no_contacts_found')}</span>
        </div>
      )}
      {contacts.length > 0 && (
        <Pagination
          currentPage={currentPage}
          lastPage={lastPage}
          totalData={contacts.length}
          onFirstPage={onFirstPage}
          onLastPage={onLastPage}
          onPreviousPage={onPreviousPage}
          onNextPage={onNextPage}
          onGoToPage={onGoToPage}
          titlePagination={t('client_access_manager_pagination_label')}
        />
      )}
      {loading && <MainSpinner />}
    </div>
  );
};

Table.defaultProps = {
  loading: false,
};

Table.propTypes = {
  loading: PropTypes.bool,
};

export default Table;
